import React from 'react'

import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  AtsFeedbackDetailFragment,
  AtsJobApplicationNotesFeedbacksFragment,
  EvaluationFormListItemFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { EvaluationFormEmptyStateMessage } from 'modules/ats/components/EvaluationForm/components/EvaluationFormEmptyStateMessage'
import { EvaluationFormItem } from 'modules/ats/components/EvaluationForm/components/EvaluationFormItem'
import { Feedback } from 'modules/ats/components/Feedbacks/Feedback'
import { FeedbackNew } from 'modules/ats/components/Feedbacks/FeedbackNew'
import { useEvaluationFormsFeatureAvailability } from 'modules/ats/hooks/features/useEvaluationFormsFeatureAvailability'

interface Props {
  application: AtsJobApplicationNotesFeedbacksFragment
  feedbacks: AtsFeedbackDetailFragment[]
  evaluationForms: EvaluationFormListItemFragment[]
  restrictedCommentsAvailable: boolean
}

export const ApplicationFeedbacks = ({
  application,
  feedbacks,
  evaluationForms,
  restrictedCommentsAvailable,
}: Props) => {
  const [showNewFeedbackEditor, setShowNewFeedbackEditor] = React.useState(false)
  const localStorageKey = `${LOCAL_STORAGE_KEYS.atsNewApplicationFeedbackDraft}:${application.id}`
  const [draftNote, setDraftNote] = useLocalStorage<string>(localStorageKey)

  const currentApplicationEvalForms = evaluationForms.filter(
    (evalForm) => evalForm.atsJobPostingId === application.jobPosting.id
  )

  const hasEvaluationForm = useEvaluationFormsFeatureAvailability()
  const showEvaluationForm = hasEvaluationForm && currentApplicationEvalForms.length > 0
  const showEvaluationFormsEmptyMessage =
    hasEvaluationForm && currentApplicationEvalForms.length === 0 && feedbacks.length === 0
  const currentApplicationFeedbacks = feedbacks.filter(
    (feedback) => feedback?.application?.id === application.id
  )
  const otherCandidateApplicationsFeedbacks = feedbacks.filter(
    (feedback) => feedback?.application?.id !== application.id
  )

  return (
    <Box gap="s32">
      <>
        {!showNewFeedbackEditor && !draftNote && (
          <Box gap="s12" justifyContent="spaceBetween" flexDirection="row" alignItems="center">
            <Text.Normal color="textPrimary" weight="semibold">
              {i18n.t('ats.application_details.notes_title')}
            </Text.Normal>
            <F0Button
              variant="outline"
              disabled={showNewFeedbackEditor}
              onClick={() => setShowNewFeedbackEditor(true)}
              icon={Add}
              label={i18n.t('ats.application_details.evaluations.new_note')}
            />
          </Box>
        )}
        {showEvaluationFormsEmptyMessage && !showNewFeedbackEditor && !draftNote && (
          <EvaluationFormEmptyStateMessage />
        )}
        {showEvaluationForm && !showEvaluationFormsEmptyMessage && (
          <Box gap="s16" paddingBottom="s8">
            {currentApplicationEvalForms.map((form) => (
              <EvaluationFormItem
                key={`eval-form-${form.id}`}
                evaluationForm={form}
                application={application}
                restrictedCommentsAvailable={restrictedCommentsAvailable}
              />
            ))}
          </Box>
        )}
        {(showNewFeedbackEditor || draftNote) && (
          <FeedbackNew
            candidateId={application.candidate.id}
            application={application}
            enabledScoring={!showEvaluationForm}
            draftNote={draftNote}
            onDraftChange={setDraftNote}
            closeNote={() => {
              setShowNewFeedbackEditor(false)
              setDraftNote('')
            }}
          />
        )}
        {currentApplicationFeedbacks.map((feedback) => (
          <Feedback
            key={`application-feedback-${feedback.id}`}
            feedback={feedback}
            enabledScoring={!showEvaluationForm}
            evaluationForm={evaluationForms.find(
              (evalForm) => evalForm.id === feedback.atsEvaluationFormsId
            )}
            restrictedCommentsAvailable={restrictedCommentsAvailable}
          />
        ))}

        {otherCandidateApplicationsFeedbacks.length > 0 && (
          <Box paddingTop="s24" gap="s32">
            <Box alignItems="center">
              <Tag inline>{i18n.t('ats.application_details.evaluations.other_evaluations')}</Tag>
            </Box>
            <Box gap="s32">
              {otherCandidateApplicationsFeedbacks.map((feedback) => (
                <Feedback
                  key={`candidate-feedback-${feedback.id}`}
                  feedback={feedback}
                  enabledScoring={!showEvaluationForm}
                  evaluationForm={evaluationForms.find(
                    (evalForm) => evalForm.id === feedback.atsEvaluationFormsId
                  )}
                  restrictedCommentsAvailable={restrictedCommentsAvailable}
                />
              ))}
            </Box>
          </Box>
        )}
      </>
    </Box>
  )
}
